import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { usePlaceBase } from "@/lib/placeLang";

const deFr = (c) => (/^[AEIOU]/i.test(c) ? `d'${c}` : `de ${c}`);
const PL = {
  en: { county: (c) => `Cities & towns in ${c} County`, pick: "Pick your city or town for a local filing guide.", all: "North Carolina cities & towns", count: (n) => `${n} municipalities, each with a local filing guide.` },
  es: { county: (c) => `Ciudades y pueblos del condado de ${c}`, pick: "Elija su ciudad o pueblo para ver una guía local.", all: "Ciudades y pueblos de Carolina del Norte", count: (n) => `${n} municipios, cada uno con su guía local.` },
  fr: { county: (c) => `Villes et communes du comté ${deFr(c)}`, pick: "Choisissez votre ville ou commune pour un guide local.", all: "Villes et communes de Caroline du Nord", count: (n) => `${n} municipalités, chacune avec son guide local.` },
  ar: { county: (c) => `المدن والبلدات في مقاطعة ${c}`, pick: "اختر مدينتك أو بلدتك للاطلاع على دليل محلي.", all: "مدن وبلدات كارولاينا الشمالية", count: (n) => `${n} بلدية، لكل منها دليل محلي.` },
  hmn: { county: (c) => `Cov nroog hauv Lub Cheeb Nroog ${c}`, pick: "Xaiv koj lub nroog kom pom phau ntawv qhia hauv zos.", all: "Cov nroog hauv North Carolina", count: (n) => `${n} lub nroog, txhua lub muaj phau ntawv qhia hauv zos.` },
};

export const CountyCities = ({ countySlug, countyName, lang = "en" }) => {
  const [cities, setCities] = useState([]);
  const base = usePlaceBase();
  useEffect(() => { api.get("/places/cities", { params: { county: countySlug } }).then(({ data }) => setCities(data.cities)).catch(() => {}); }, [countySlug]);
  if (!cities.length) return null;
  return (
    <section className="max-w-5xl mx-auto px-6 py-6" data-testid="county-cities">
      <h2 className="font-serif text-3xl tracking-tight mb-2">{(PL[lang] || PL.en).county(countyName)}</h2>
      <p className="text-muted-foreground mb-5">{(PL[lang] || PL.en).pick}</p>
      <div className="flex flex-wrap gap-2">
        {cities.map((c) => (
          <Link key={c.slug} to={`${base}/${countySlug}/${c.slug}`} data-testid={`county-city-${c.slug}`}
            className="border border-border px-3 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground transition-colors">{c.name}</Link>
        ))}
      </div>
    </section>
  );
};

export const AllCities = ({ lang = "en" }) => {
  const [cities, setCities] = useState([]);
  const base = usePlaceBase();
  useEffect(() => { api.get("/places/cities").then(({ data }) => setCities(data.cities)).catch(() => {}); }, []);
  const groups = useMemo(() => cities.reduce((acc, c) => { (acc[c.name[0].toUpperCase()] ||= []).push(c); return acc; }, {}), [cities]);
  if (!cities.length) return null;
  return (
    <section className="max-w-6xl mx-auto px-6 pb-20" data-testid="all-cities">
      <h2 className="font-serif text-3xl tracking-tight mb-2">{(PL[lang] || PL.en).all}</h2>
      <p className="text-muted-foreground mb-6">{(PL[lang] || PL.en).count(cities.length)}</p>
      <div className="space-y-6">
        {Object.keys(groups).sort().map((letter) => (
          <div key={letter}>
            <p className="font-serif text-xl text-accent mb-2">{letter}</p>
            <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-sm">
              {groups[letter].map((c) => (
                <Link key={`${c.county_slug}-${c.slug}`} to={`${base}/${c.county_slug}/${c.slug}`} data-testid={`all-city-${c.slug}`}
                  className="text-muted-foreground hover:text-accent transition-colors">{c.name}</Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
